import { useState, type FC } from "react";
import ReportQualitySemaphore from "./ReportQualitySemaphore";
import type { SemaforoNivel, CriterioResultado } from "./ReportQualitySemaphore";

interface SeccionDraft {
  titulo: string;
  contenido: string;
}

interface Props {
  secciones: SeccionDraft[];
  solicitante: string;
  area: string;
  estudiante: string;
  curso: string;
  profesor: string;
  periodo: string;
  calidadNivel: SemaforoNivel;
  calidadCriterios: CriterioResultado[];
  onSubmit: () => void;
  onSaveDraft: () => void;
  onEdit: () => void;
  onDownload?: () => void;
  className?: string;
  isSubmitting?: boolean;
}

const ReportDraftPreview: FC<Props> = ({
  secciones,
  solicitante,
  area,
  estudiante,
  curso,
  profesor,
  periodo,
  calidadNivel,
  calidadCriterios,
  onSubmit,
  onSaveDraft,
  onEdit,
  onDownload,
  className = "",
  isSubmitting = false,
}) => {
  const [showFull, setShowFull] = useState(false);

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Quality semaphore */}
      <ReportQualitySemaphore nivel={calidadNivel} criterios={calidadCriterios} />

      {/* Draft preview */}
      <div className="card-modern overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200/60 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-foreground-900">Vista previa del informe</h3>
            <p className="text-xs text-foreground-400 mt-0.5">Revise el borrador antes de enviar</p>
          </div>
          <button
            onClick={() => setShowFull(!showFull)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer flex items-center gap-1"
          >
            {showFull ? "Contraer" : "Ver completo"}
            <span className="flex items-center justify-center w-3 h-3">
              <i className={`ri-arrow-${showFull ? "up" : "down"}-s-line`} />
            </span>
          </button>
        </div>

        <div className={`px-5 py-4 overflow-hidden transition-all duration-300 ${showFull ? "max-h-[2000px]" : "max-h-[240px]"}`}>
          <div className="prose prose-sm max-w-none">
            {/* Header */}
            <div className="text-center mb-6 pb-4 border-b border-background-200/60">
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-1">Informe de Antecedentes Docentes</p>
              <p className="text-[11px] text-foreground-500">Solicitado por: {solicitante} • {area}</p>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-2 mb-5 text-xs">
              <div className="p-2.5 rounded-lg bg-background-50/80 border border-background-200/40">
                <p className="font-bold text-foreground-400 uppercase text-[10px] tracking-wider">Estudiante</p>
                <p className="text-foreground-800 font-medium mt-0.5">{estudiante}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-background-50/80 border border-background-200/40">
                <p className="font-bold text-foreground-400 uppercase text-[10px] tracking-wider">Curso</p>
                <p className="text-foreground-800 font-medium mt-0.5">{curso}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-background-50/80 border border-background-200/40">
                <p className="font-bold text-foreground-400 uppercase text-[10px] tracking-wider">Docente informante</p>
                <p className="text-foreground-800 font-medium mt-0.5">{profesor}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-background-50/80 border border-background-200/40">
                <p className="font-bold text-foreground-400 uppercase text-[10px] tracking-wider">Periodo</p>
                <p className="text-foreground-800 font-medium mt-0.5">{periodo}</p>
              </div>
            </div>

            {/* Secciones */}
            {secciones.map((sec, idx) => (
              <div key={idx} className="mb-4">
                <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-1.5">{sec.titulo}</h4>
                <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{sec.contenido || "Sin información registrada."}</p>
              </div>
            ))}
          </div>

          {!showFull && (
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={onEdit}
          className="btn-modern bg-background-100 text-foreground-700 hover:bg-background-200 active:scale-[0.98] px-5 py-2.5 text-sm cursor-pointer flex-1 sm:flex-initial"
        >
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-edit-line" />
          </span>
          Editar borrador
        </button>
        <button
          onClick={onSaveDraft}
          className="btn-modern bg-secondary-500 text-white hover:bg-secondary-600 active:scale-[0.98] px-5 py-2.5 text-sm cursor-pointer flex-1 sm:flex-initial"
        >
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-save-3-line" />
          </span>
          Guardar borrador
        </button>
        {onDownload && (
          <button
            onClick={onDownload}
            className="btn-modern bg-background-100 text-foreground-700 hover:bg-background-200 active:scale-[0.98] px-5 py-2.5 text-sm cursor-pointer flex-1 sm:flex-initial"
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-download-2-line" />
            </span>
            Descargar copia
          </button>
        )}
        <button
          onClick={onSubmit}
          disabled={calidadNivel === "rojo" || isSubmitting}
          className={`btn-modern-primary px-5 py-2.5 text-sm cursor-pointer flex-1 sm:flex-initial ${
            calidadNivel === "rojo" ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-send-plane-line" />
              </span>
              Enviar a revisión
            </>
          )}
        </button>
      </div>

      {calidadNivel === "rojo" && (
        <div className="alert-card bg-red-50 border-red-100 p-4">
          <div className="flex items-start gap-2.5">
            <span className="flex items-center justify-center w-5 h-5 flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-red-500" />
            </span>
            <div>
              <p className="text-xs font-bold text-red-700">El informe no puede enviarse en este estado</p>
              <p className="text-xs text-red-600 mt-0.5">
                Corrija las frases marcadas en rojo y asegúrese de que todas las secciones tengan información suficiente antes de enviar.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDraftPreview;